import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getReviews as fetchReviews, createReview, updateReview, deleteReview } from '@/lib/api/reviews';
import { logActivity as logActivityDb } from '@/lib/api/activity';
import { refreshStore } from '@/lib/store';
import type { Review as DbReview } from '@/types/db';

// Local view-model: keeps `date` so the existing form/UI is untouched.
interface Review {
  id: string;
  name: string;
  location: string;
  rating: number;
  text: string;
  treatment: string;
  date: string;
  visible: boolean;
  verified: boolean;
}

function toVM(r: DbReview): Review {
  return {
    id: r.id,
    name: r.name,
    location: r.location ?? '',
    rating: r.rating,
    text: r.text,
    treatment: r.treatment ?? '',
    date: r.review_date ?? '',
    visible: r.visible,
    verified: r.verified,
  };
}

function toDb(r: Review): Partial<DbReview> {
  return {
    name: r.name,
    location: r.location,
    rating: r.rating,
    text: r.text,
    treatment: r.treatment,
    review_date: r.date || null,
    visible: r.visible,
    verified: r.verified,
  };
}

const NEW_ID = '__new__';

export default function ReviewsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [reviews, setReviews] = useState<Review[]>([]);
  const [editing, setEditing] = useState<Review | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [filterVisible, setFilterVisible] = useState<'all' | 'visible' | 'hidden'>('all');
  const [savedMsg, setSavedMsg] = useState('');

  useEffect(() => {
    if (sessionStorage.getItem('admin_logged_in') !== 'true') {
      navigate('/admin');
      return;
    }
    loadReviews();
  }, [navigate]);

  const loadReviews = async () => {
    try {
      const data = await fetchReviews();
      setReviews(data.map(toVM));
    } catch {
      setReviews([]);
    }
  };

  const afterWrite = async (detail: string) => {
    await logActivityDb('reviews_updated', detail);
    await refreshStore();
    await loadReviews();
    setSavedMsg(t('adm1.saved'));
    setTimeout(() => setSavedMsg(''), 2000);
  };

  const handleAddNew = () => {
    setEditing({
      id: NEW_ID,
      name: '',
      location: '',
      rating: 5,
      text: '',
      treatment: '',
      date: new Date().toISOString().split('T')[0],
      visible: true,
      verified: true,
    });
    setShowModal(true);
  };

  const handleEdit = (r: Review) => {
    setEditing({ ...r });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!editing) return;
    try {
      if (editing.id === NEW_ID) {
        await createReview(toDb(editing));
      } else {
        await updateReview(editing.id, toDb(editing));
      }
      await afterWrite('Bewertung gespeichert');
    } catch {
      setSavedMsg(t('adm1.save_error'));
    }
    setShowModal(false);
    setEditing(null);
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteReview(id);
      await afterWrite('Bewertung gelöscht');
    } catch { /* ignore */ }
    setDeleteConfirm(null);
  };

  const handleToggleVisibility = async (id: string) => {
    const r = reviews.find((x) => x.id === id);
    if (!r) return;
    try {
      await updateReview(id, { visible: !r.visible });
      await afterWrite('Sichtbarkeit geändert');
    } catch { /* ignore */ }
  };

  const filtered = reviews.filter((r) => {
    if (filterVisible === 'visible') return r.visible;
    if (filterVisible === 'hidden') return !r.visible;
    return true;
  });

  const renderStars = (rating: number) => {
    return Array.from({ length: 5 }).map((_, i) => (
      <i key={i} className={`ri-star-fill text-xs ${i < rating ? 'text-amber-400' : 'text-foreground-200'}`}></i>
    ));
  };

  if (sessionStorage.getItem('admin_logged_in') !== 'true') return null;

  return (
    <div className="px-4 py-6 md:px-6 space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="font-heading text-xl font-bold text-foreground-900">{t('adm1.reviews_title')}</h1>
          <p className="mt-0.5 text-xs text-foreground-500 font-body">
            {t('adm1.reviews_summary', { total: reviews.length, visible: reviews.filter((r) => r.visible).length })}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {savedMsg && <span className="text-xs text-green-600 font-label">{savedMsg}</span>}
          <button
            onClick={handleAddNew}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-primary-500 text-white text-xs font-label hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-add-line"></i>
            {t('adm1.reviews_new')}
          </button>
        </div>
      </div>

      {/* ── Filter ──────────────────────────────────── */}
      <div className="flex items-center gap-2 flex-wrap">
        {[
          { value: 'all', label: t('adm1.filter_all') },
          { value: 'visible', label: t('adm1.filter_visible') },
          { value: 'hidden', label: t('adm1.filter_hidden') },
        ].map((f) => (
          <button
            key={f.value}
            onClick={() => setFilterVisible(f.value as 'all' | 'visible' | 'hidden')}
            className={`px-3 py-2 rounded-full text-xs font-label transition-all cursor-pointer whitespace-nowrap ${
              filterVisible === f.value
                ? 'bg-foreground-900 text-white'
                : 'bg-background-50 border border-background-200/70 text-foreground-500 hover:text-foreground-700'
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* ── Reviews List ────────────────────────────── */}
      <div className="space-y-3">
        {filtered.map((review) => (
          <div key={review.id} className={`bg-background-50 rounded-xl border transition-all ${review.visible ? 'border-background-200/70' : 'border-background-200/40 opacity-60'}`}>
            <div className="p-5">
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-4 flex-1 min-w-0">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 flex-shrink-0">
                    <span className="text-sm font-label font-bold">{review.name.charAt(0)}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-label text-sm font-semibold text-foreground-900">{review.name}</h3>
                      {review.verified && (
                        <span className="inline-flex items-center gap-0.5 text-green-600 text-[10px] font-label">
                          <i className="ri-verified-badge-fill text-xs"></i>
                        </span>
                      )}
                      <span className="text-[10px] text-foreground-400 font-body">{review.location}</span>
                    </div>
                    <div className="flex items-center gap-1 mt-1">
                      {renderStars(review.rating)}
                      <span className="text-[10px] text-foreground-400 font-body ml-1.5">{review.treatment}</span>
                    </div>
                    <p className="mt-2 text-xs text-foreground-600 font-body leading-relaxed">{review.text}</p>
                    <p className="mt-1 text-[10px] text-foreground-400 font-body">{review.date}</p>
                  </div>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0 ml-4">
                  <button
                    onClick={() => handleToggleVisibility(review.id)}
                    className={`w-7 h-7 flex items-center justify-center rounded-md transition-colors cursor-pointer ${
                      review.visible ? 'hover:bg-background-100 text-green-500' : 'hover:bg-background-100 text-foreground-300'
                    }`}
                    title={review.visible ? t('adm1.action_hide') : t('adm1.action_show')}
                  >
                    <i className={`ri-${review.visible ? 'eye' : 'eye-off'}-line text-xs`}></i>
                  </button>
                  <button
                    onClick={() => handleEdit(review)}
                    className="w-7 h-7 flex items-center justify-center rounded-md hover:bg-background-100 text-foreground-400 hover:text-primary-600 transition-colors cursor-pointer"
                    title={t('adm1.action_edit')}
                  >
                    <i className="ri-edit-line text-xs"></i>
                  </button>
                  <button
                    onClick={() => setDeleteConfirm(review.id)}
                    className="w-7 h-7 flex items-center justify-center rounded-md hover:bg-red-50 text-foreground-400 hover:text-red-500 transition-colors cursor-pointer"
                    title={t('adm1.action_delete')}
                  >
                    <i className="ri-delete-bin-line text-xs"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}
        {filtered.length === 0 && (
          <div className="text-center py-16">
            <i className="ri-chat-quote-line text-3xl text-foreground-300"></i>
            <p className="mt-3 text-sm text-foreground-400 font-body">{t('adm1.reviews_empty_title')}</p>
            <p className="mt-1 text-xs text-foreground-400 font-body">{t('adm1.reviews_empty_desc')}</p>
          </div>
        )}
      </div>

      {/* ── Edit Modal ──────────────────────────────── */}
      {showModal && editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => { setShowModal(false); setEditing(null); }}>
          <div className="bg-background-50 rounded-2xl border border-background-200/70 w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="px-6 py-5 border-b border-background-100 flex items-center justify-between">
              <h2 className="font-label text-sm font-semibold text-foreground-900">
                {editing.name ? t('adm1.reviews_edit') : t('adm1.reviews_new')}
              </h2>
              <button onClick={() => { setShowModal(false); setEditing(null); }} className="w-7 h-7 flex items-center justify-center rounded-md hover:bg-background-100 text-foreground-400 transition-colors cursor-pointer">
                <i className="ri-close-line"></i>
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-label font-semibold text-foreground-700 mb-1.5">{t('adm1.label_name')}</label>
                  <input type="text" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 font-body focus:outline-none focus:border-primary-400 transition-all" />
                </div>
                <div>
                  <label className="block text-xs font-label font-semibold text-foreground-700 mb-1.5">{t('adm1.label_location')}</label>
                  <input type="text" value={editing.location} onChange={(e) => setEditing({ ...editing, location: e.target.value })} className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 font-body focus:outline-none focus:border-primary-400 transition-all" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-label font-semibold text-foreground-700 mb-1.5">{t('adm1.label_treatment')}</label>
                  <input type="text" value={editing.treatment} onChange={(e) => setEditing({ ...editing, treatment: e.target.value })} className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 font-body focus:outline-none focus:border-primary-400 transition-all" />
                </div>
                <div>
                  <label className="block text-xs font-label font-semibold text-foreground-700 mb-1.5">{t('adm1.label_date')}</label>
                  <input type="date" value={editing.date} onChange={(e) => setEditing({ ...editing, date: e.target.value })} className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 font-body focus:outline-none focus:border-primary-400 transition-all" />
                </div>
              </div>
              <div>
                <label className="block text-xs font-label font-semibold text-foreground-700 mb-1.5">{t('adm1.label_rating')}</label>
                <div className="flex items-center gap-1">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      onClick={() => setEditing({ ...editing, rating: star })}
                      className="cursor-pointer"
                    >
                      <i className={`text-lg ${star <= editing.rating ? 'ri-star-fill text-amber-400' : 'ri-star-line text-foreground-300'}`}></i>
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-xs font-label font-semibold text-foreground-700 mb-1.5">{t('adm1.label_text')}</label>
                <textarea value={editing.text} onChange={(e) => setEditing({ ...editing, text: e.target.value })} rows={4} className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 font-body focus:outline-none focus:border-primary-400 transition-all resize-y" />
              </div>
              <div className="flex items-center gap-6">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={editing.visible} onChange={(e) => setEditing({ ...editing, visible: e.target.checked })} className="rounded border-background-300 text-primary-500 focus:ring-primary-400 cursor-pointer" />
                  <span className="text-xs font-label text-foreground-700">{t('adm1.label_visible')}</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={editing.verified} onChange={(e) => setEditing({ ...editing, verified: e.target.checked })} className="rounded border-background-300 text-primary-500 focus:ring-primary-400 cursor-pointer" />
                  <span className="text-xs font-label text-foreground-700">{t('adm1.label_verified')}</span>
                </label>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-background-100 flex items-center justify-end gap-3">
              <button onClick={() => { setShowModal(false); setEditing(null); }} className="px-4 py-2 rounded-lg text-xs font-label text-foreground-500 hover:text-foreground-700 transition-colors cursor-pointer whitespace-nowrap">{t('adm1.cancel')}</button>
              <button onClick={handleSave} className="px-5 py-2 rounded-lg bg-primary-500 text-white text-xs font-label hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap">{t('adm1.save')}</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Delete Confirm ──────────────────────────── */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setDeleteConfirm(null)}>
          <div className="bg-background-50 rounded-2xl border border-background-200/70 w-full max-w-sm mx-4 p-6" onClick={(e) => e.stopPropagation()}>
            <div className="w-10 h-10 flex items-center justify-center rounded-full bg-red-100 text-red-600 mx-auto mb-3">
              <i className="ri-error-warning-line text-lg"></i>
            </div>
            <h3 className="text-center font-label text-sm font-semibold text-foreground-900">{t('adm1.reviews_delete_confirm')}</h3>
            <p className="mt-1 text-center text-xs text-foreground-500 font-body">{t('adm1.delete_irreversible')}</p>
            <div className="flex items-center gap-3 mt-5">
              <button onClick={() => setDeleteConfirm(null)} className="flex-1 px-4 py-2 rounded-lg border border-background-200 text-xs font-label text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap">{t('adm1.cancel')}</button>
              <button onClick={() => handleDelete(deleteConfirm)} className="flex-1 px-4 py-2 rounded-lg bg-red-500 text-white text-xs font-label hover:bg-red-600 transition-colors cursor-pointer whitespace-nowrap">{t('adm1.delete')}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}